import { useState, useEffect } from "react";
import { Button, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import DropzoneArea from "components/DropzoneArea";
import { formatBytes } from "utils";
import { Dictionary } from "types";
import { useFileUploader } from "hooks";
import { FileStatusDisplay, FileStatusType } from "components/FileStatusDisplay"
import Feedback, { FeedbackProps } from "components/@beca-common-react-legacy/Feedback";
import { useParams } from "react-router-dom";
import { useView, ViewConfig } from "hooks/queries/useView";
import { PermissionWrapper } from "utils/beyonAuth/PermissionWrapper";
import { EditForm } from "components/Form/EditForm";
import { IndexableDocument } from "hooks/queries/useDocuments";
import { useWorkspace } from "hooks/queries/useWorkspace";
import { useBatch } from "hooks/queries/useBatch";
import { WorkspaceConfig } from "types/WorkspaceConfig";
import { axiosBeyonClient } from "utils/beyonAuth/beyonAxiosClient";
import { QueryAlert } from "utils/queryAlert";
import { useMutation, UseMutationResult } from "@tanstack/react-query";
import { Tag } from "types/Tag";
import { useTags } from "hooks/queries/useTags";
import { CheckCircle } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";

export type UploadFile = Omit<IndexableDocument, "id"> & {
    status: FileStatusType;
    progress: number;
};

export default function Uploader() {

    const { workspaceId, viewId } = useParams<{ workspaceId: string; viewId: string; }>();
    const workspaceQuery = useWorkspace(workspaceId);
    const workspaceConfig = workspaceQuery.data?.config;
    const connectorConfig = workspaceConfig?.connectorConfig;
    const tagConfig = workspaceConfig?.tagConfig;

    const viewQuery = useView(workspaceId, viewId);
    const viewConfig = viewQuery?.data;
    const uploadPropertiesConfig = viewConfig?.uploadConfig?.properties;

    const [step, setStep] = useState<"upload" | "success">("upload");

    const { maxFileCount = 10, maxFileSize = 100000, fileTypes = [] } = useView(workspaceId, viewId).data?.uploadConfig || {};

    const [files, setFiles] = useState<Dictionary<UploadFile>>({});

    const [documentData, setDocumentData] = useState<Record<string, any> & { tags?: Tag[] }>();
    const [feedback, setFeedback] = useState<FeedbackProps>();

    const { uploadFile } = useFileUploader(workspaceId);

    const batchMutation = useBatch(workspaceQuery?.data?.config);

    const tags = useTags({ workspaceId, workspaceConfig, enabled: !!tagConfig });

    const saveMutation = useMutation({
        mutationFn: onSave
    });

    useEffect(() => {
        if (workspaceConfig && viewConfig && !documentData)
            setDocumentData(getDefaultFormData(workspaceConfig, viewConfig));
    }, [workspaceConfig, viewConfig, documentData]);

    return (
        <QueryAlert queries={[workspaceQuery, viewQuery]} mutations={[saveMutation as UseMutationResult]}>
            <PermissionWrapper permission="EditResourceData" workspaceId={workspaceId} fallback>

                <Stack gap={1} margin={2} m={4}>
                    <Feedback {...feedback} />
                    {step === "upload" &&
                        <>
                            <Typography variant="h4">Upload documents</Typography>

                            <EditForm
                                item={documentData || {}}
                                properties={workspaceConfig?.propertiesSchema?.filter(x => uploadPropertiesConfig.includes(x.name))}
                                enableTags={tagConfig && viewConfig?.uploadConfig?.enableTags}
                                tagOptions={tags?.data}
                                onChange={setDocumentData}
                            />

                            <DropzoneArea
                                onAccept={onAccept}
                                onError={(error: Error) => setFeedback({ message: error.message, severity: "error", neverHide: true })}
                                fileTypes={fileTypes.join(",")}
                                filesLimit={maxFileCount}
                                maxFileSize={maxFileSize}
                            />

                            <Stack direction="row" gap={1} justifyContent="end">
                                <Button
                                    color="primary"
                                    variant="outlined"
                                    onClick={window.close}
                                    disabled={saveMutation.isPending}
                                >
                                    Cancel
                                </Button>
                                <LoadingButton
                                    color="primary"
                                    variant="contained"
                                    onClick={async () => saveMutation.mutateAsync()}
                                    disabled={Object.values(files).length === 0 || !Object.values(files).every(x => x.status === "success")}
                                    loading={saveMutation.isPending}
                                >
                                    Save
                                </LoadingButton>
                            </Stack>

                            {Object.keys(files).length > 0 &&
                                <>
                                    <Typography variant="h5">Uploaded files</Typography>
                                    <FileTable files={files} />
                                </>}
                        </>
                    }
                    {step === "success" && <Stack gap={4}>
                        <Stack alignSelf="center">
                            <CheckCircle sx={{ fontSize: 120 }} color="success" />
                        </Stack>

                        <Typography textAlign="center" variant="h5">Your files have been uploaded successfully. You can now close this window.</Typography>

                        <Stack alignSelf="end">
                            <Button variant='outlined' onClick={() => window.close()}>Close</Button>
                        </Stack>
                    </Stack>}
                </Stack>
            </PermissionWrapper>
        </QueryAlert>
    );

    async function onSave() {

        // prepare the batch request
        const filesArray = Object.values(files);
        const requests = filesArray.map((file) => {

            const payload = getPostPayload(workspaceConfig, viewConfig, file, documentData);

            if (tagConfig) {
                if (!tagConfig.documentTagCollectionProperty || !tagConfig.tagDocumentIdProperty || !tagConfig.tagValueProperty)
                    throw new Error("TagConfiguration is invalid or incomplete");

                payload[tagConfig.documentTagCollectionProperty] = (documentData?.tags || []).map(x => ({
                    [tagConfig.tagValueProperty]: x.value
                }))
            }

            return {
                method: "POST",
                url: connectorConfig.entity,
                body: payload
            };
        });

        // do the batch request
        const batchResult = await batchMutation.mutateAsync(requests);

        //check for batch errors
        if (batchResult.responses.some(x => x.status !== 200 && x.status !== 201))
            throw new Error("An unexpected error occured while creating the documents");

        // do the filestore publish requests
        // TODO make this to run in a batch
        const publishPromises = filesArray.map(async file =>
            axiosBeyonClient.put(`${workspaceConfig.filestoreConfig.apiEndpoint}/file/${file.fileId}/publish`)
        )
        await Promise.all(publishPromises)

        setStep("success");
    }

    async function onAccept(acceptedFiles: File[]) {

        acceptedFiles.forEach(droppedFile => {
            files[droppedFile.name] = {
                fileId: '',
                title: droppedFile.name,
                name: droppedFile.name,
                type: droppedFile.type,
                file: droppedFile,
                status: "queued",
                progress: 0,
                size: droppedFile.size
            }
        });

        setFiles({ ...files });

        await acceptedFiles.forEach(async file => {

            try {
                await uploadFile(file,
                    {
                        onStart: () => setStatus(file, "queued"),
                        onProgress: (progress) => setProgress(file, progress),
                        onComplete: (fileId) => {
                            setStatus(file, "success");
                            updateFile({
                                ...files[file.name],
                                fileId
                            });
                        }
                    });
            } catch (error) {
                setStatus(file, "error")
            }
        });

    }

    function setStatus(file: File, status: FileStatusType) {
        updateFile({
            ...files[file.name],
            status
        });
    }

    function setProgress(file: File, progress: number) {
        updateFile({
            ...files[file.name],
            status: "uploading",
            progress
        });
    }

    function updateFile(uploadFile: UploadFile) {
        files[uploadFile.file.name] = {
            ...files[uploadFile.file.name],
            ...uploadFile
        };
        setFiles({ ...files });
    }
}

function FileTable({ files }: { files: Dictionary<UploadFile> }) {
    return (
        <TableContainer >
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>File</TableCell>
                        <TableCell align="right">Size</TableCell>
                        <TableCell align="right"></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Object.values(files).map((x) => (
                        <TableRow key={x.file.name} >
                            <TableCell>
                                <Typography overflow="hidden" textOverflow="ellipsis" width="230px">
                                    {x.file.name}
                                </Typography>
                            </TableCell>
                            <TableCell align="right">{formatBytes(x.file.size, 0)}</TableCell>
                            <TableCell align="right">
                                <FileStatusDisplay status={x.status} progress={x.progress} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function getPostPayload(workspaceConfig: WorkspaceConfig, viewConfig: ViewConfig, file: UploadFile, documentData: Record<string, any>) {

    const payload: Record<string, any> = {
        [workspaceConfig.connectorConfig.titleProperty]: file.title,
        [workspaceConfig.connectorConfig.fileNameProperty]: file.name,
        [workspaceConfig.connectorConfig.fileIdProperty]: file.fileId,
        [workspaceConfig.connectorConfig.typeProperty]: file.type
    };

    viewConfig.uploadConfig.properties.forEach(viewProperty => {
        const workspaceProperty = workspaceConfig.propertiesSchema.find(x => x.name === viewProperty);
        if (!workspaceProperty)
            return;

        payload[viewProperty] = documentData[viewProperty];
    })

    if (!payload[workspaceConfig.connectorConfig.titleProperty])
        payload[workspaceConfig.connectorConfig.titleProperty] = file.title;

    if (workspaceConfig.connectorConfig.fileSizeProperty !== undefined &&
        !payload[workspaceConfig.connectorConfig.fileSizeProperty]) {
        payload[workspaceConfig.connectorConfig.fileSizeProperty] = file.size;
    }
    return payload;
}

export function getDefaultFormData(workspaceConfig: WorkspaceConfig, viewConfig: ViewConfig) {

    const data: Record<string, any> & { tags?: Tag[] } = {};

    viewConfig.uploadConfig.properties.forEach(viewProperty => {
        const workspaceProperty = workspaceConfig.propertiesSchema.find(x => x.name === viewProperty);
        if (!workspaceProperty)
            return;

        if (workspaceProperty.default)
            data[workspaceProperty.name] = workspaceProperty.default;
    });

    return data;

}